import { Crosshair } from "lucide-react";
import { MemberFirearmDTO } from "../../services/apiClient";
import { convertToUserTime } from "../../utils/dateUtils/convertToUserTime";
import { getStatus } from "../../utils/firearmUtils";
import ImagePreview from "../common/ImagePreview";
import { FirearmOwnerConstants } from "../../constants/FirearmOwnerConstants";

export default function FirearmOverview({
    items,
    showExpiringSoon = true,
    gridLayout = "two-rows"
}: {
    items: MemberFirearmDTO[],
    showExpiringSoon?: boolean,
    gridLayout?: "single-row" | "two-rows" | "responsive"
}) {
    // Filter to only show user's own firearms
    const filteredItems = items.filter(item => item.firearmOwnerId === FirearmOwnerConstants.MY_FIREARM && item.licenceExpiryDate);

    // 1. Derive counts
    const counts = { expired: 0, expiring: 0, renewal: 0, active: 0 };
    const soon: MemberFirearmDTO[] = [];

    for (const m of filteredItems) {
        const s = getStatus(m); // already available in your code:contentReference[oaicite:0]{index=0}
        if (s === "submitted") counts.renewal++;
        else if (s === "expired") counts.expired++;
        else if (s === "expiring") counts.expiring++;
        else if (s === "active") counts.active++;

        if (s === "expired" || s === "expiring") soon.push(m);
    }

    soon.sort(
        (a, b) =>
            new Date(a.licenceExpiryDate ?? 0).getTime() -
            new Date(b.licenceExpiryDate ?? 0).getTime()
    );

    // 2. Render
    return (
        <div className="space-y-6">
            {/* Status grid */}
            <div className="rounded-2xl bg-white p-4 shadow">
                <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-sm font-semibold">FIREARM STATUS</h3>
                    <span className="rounded-full border px-2 py-0.5 text-xs">Attention</span>
                </div>
                <div className={
                    gridLayout === "single-row"
                        ? "grid grid-cols-4 gap-3"
                        : gridLayout === "responsive"
                            ? "grid grid-cols-4 md:grid-cols-2 gap-3"
                            : "grid grid-cols-2 gap-3"
                }>
                    <StatBox label="EXPIRED" value={counts.expired} color="text-red-600" />
                    <StatBox label="EXPIRING" value={counts.expiring} color="text-yellow-600" />
                    <StatBox label="RENEWAL" value={counts.renewal} color="text-green-600" />
                    <StatBox label="ACTIVE" value={counts.active} color="text-green-600" />
                </div>
            </div>

            {/* Expired / Expiring Soon list */}
            {showExpiringSoon && (
                <div className="rounded-2xl bg-white p-4 shadow hidden md:block">
                    <h3 className="mb-3 text-sm font-semibold">EXPIRED / EXPIRING SOON</h3>
                    <ul className="space-y-3">
                        {soon.slice(0, 5).map((m) => (
                            <li key={m.id} className="flex items-start justify-between">
                                <div className="flex items-start gap-3">
                                    {m.firearmFileId ? (
                                        <ImagePreview fileId={m.firearmFileId} size="sm" />
                                    ) : (
                                        <div className="h-10 w-10 flex items-center justify-center rounded bg-gray-100">
                                            <Crosshair className="w-6 h-6 text-gray-600" />
                                        </div>
                                    )}
                                    <div>
                                        <div className="text-sm font-semibold">
                                            {[m.firearmMake?.title, m.caliber?.title].filter(Boolean).join(" - ") || "Unknown"}
                                        </div>
                                        <div className={`text-xs ${getStatus(m) === "expired" ? "text-red-600" : "text-yellow-600"}`}>
                                            {getStatus(m) === "expired" ? "Expired" : "Expiring"}:{" "}
                                            {m.licenceExpiryDate
                                                ? convertToUserTime(String(m.licenceExpiryDate), true)
                                                : "n/a"}
                                        </div>
                                    </div>
                                </div>
                                <button className="text-gray-400">⋯</button>
                            </li>
                        ))}
                        {soon.length === 0 && <li className="text-xs text-gray-500">Nothing to show</li>}
                    </ul>
                </div>
            )}
        </div>
    );
}

function StatBox({ label, value, color }: { label: string; value: number; color: string }) {
    return (
        <div className="rounded-lg bg-gray-50 p-4 text-center">
            <div className={`text-xl font-bold ${color}`}>{value}</div>
            <div className="text-xs text-gray-600">{label}</div>
        </div>
    );
}
